import {React, observer, classNames, useEffect} from '@api'
import {Classes, Dialog} from '@blueprintjs/core'
import {ErrorBoundary, RightSidebar, useStores} from '@modules'
import {ReportPropertiesPanel} from '@stores/catalog/flexLayout'
import {ErrorOnFlexLayoutLoadContainer} from '@stores/catalog/flexLayout/flexComponents/ErrorOnFlexLayoutLoadContainer'
import {useHotkeys} from 'react-hotkeys-hook'
import * as css from './FlexLayout.css'
import {BorderNode, FlexLayout as FL, SplitterNode, TabNode, TabSetNode} from '@flexlayout'
import {FlexLayoutStoreContext, FlexLayoutPreview} from './'

export interface IFlexLayoutTabProps {
  flexNode: TabNode | TabSetNode | BorderNode | SplitterNode
  // deployment?: string
  // workspace?: string
  isLayoutPreview?: boolean
}

/// region Components

interface MyProps {
  className?: string
}

/** Root Component wrapping a FlexLayout Layout component allowing for dockable panels, tabs, arbitrary splits, etc
 *
 */
export const FlexLayout = observer<MyProps>(function FlexLayout({className, ...props}) {
  const {
    reports, flexLayout, flexLayout: {cssVariables, visible, previewPreset, previewLayoutInteractionNode, settings, renderNamedComponent, setRef, onModelChange, onRenderTab, onRenderTabSet, model, onAction, iconFactory},
    reports: {currentReport, saveAsDialogReport, isSaveAsDialogOpen, cancelReportDialog, stores: {currentUser: {userSettings: {focusMode}, anyFocusModeItemsExplicitlyChecked}}}
  } = useStores()

  // useEffect(() => {
  //   console.log(`Mount FlexLayout`)
  //   return () => console.log(`Unmount FlexLayout`)
  // }, [])

  useHotkeys('option+r', () => {
    const {activeTab} = flexLayout
    if (activeTab && activeTab.parent) {
      flexLayout.onStartRenameTab(activeTab)
    }
  })

  useHotkeys('option+d', () => {
    const {activeTab} = flexLayout
    if (activeTab && activeTab.parent) {
      activeTab.duplicate('inline')
    }
  })

  // useHotkeys('option+left', (e) => {
  //   e.stopPropagation()
  //   const {model: {activeTabset}} = flexLayout
  //   activeTabset?.selectPreviousTab()
  // })

  useHotkeys('option+shift+up', (e) => {
    e.stopPropagation()
    const {activeTab} = flexLayout
    activeTab?.maximize()
  })
  useHotkeys('option+shift+down', (e) => {
    e.stopPropagation()
    const {toggleMaximize, maximizedTab} = flexLayout
    maximizedTab && toggleMaximize(maximizedTab)
  })

  useHotkeys('option+m', (e) => {
    e.stopPropagation()
    const {activeTab} = flexLayout
    activeTab?.toggleMaximize()
  })

  // useHotkeys('option+right', (e) => {
  //   e.stopPropagation()
  //   const {model: {activeTabset}} = flexLayout
  //   activeTabset?.selectNextTab()
  // })

  useHotkeys('option+left, option+shift+left', e => {
    e.stopPropagation()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      //moveTab(activeTab, 'left')
      activeTab.leftOrTop?.select?.()
    }
  })

  useHotkeys('option+right, option+shift+right', e => {
    e.stopPropagation()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      //moveTab(activeTab, 'right')
      activeTab.rightOrBottom?.select?.()
    }
  })

  useHotkeys('option+up', e => {
    e.stopPropagation()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      moveTab(activeTab, 'up')
    }
  })

  useHotkeys('option+down', e => {
    e.stopPropagation()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      moveTab(activeTab, 'down')
    }
  })

  useHotkeys('shift+option+tab', e => {
    e.stopPropagation()
    e.preventDefault()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      activeTab.tabSet.selectPreviousTab()
    }
  })

  useHotkeys('option+tab', e => {
    e.stopPropagation()
    e.preventDefault()
    const {activeTab, isMaximized, moveTab} = flexLayout
    if (activeTab && !isMaximized) {
      activeTab.tabSet.selectNextTab()
    }
  })

  useHotkeys('option+z', e => {
    e.stopPropagation()
    e.preventDefault()
    const {currentReport} = reports
    model?.canUndo && model.undo()
  })

  useHotkeys('option+shift+z', e => {
    e.stopPropagation()
    e.preventDefault()
    model?.canRedo && model.redo()
  })

  useHotkeys('option+esc', e => {
    e.stopPropagation()
    e.preventDefault()
    const {currentReport} = reports
    if (currentReport) { model.historyIndex = -1 }
  })

  const {draggingNode, maximizedTab, maximizedTabSet} = flexLayout

  // trace()
  // console.log(`Render FlexLayout container`, {maximizedTab: maximizedTab?.id, model, visible})
  return (
    <FlexLayoutStoreContext.Provider value={flexLayout}>
      <div className={classNames(css.root, className)}
           data-maximized-tab={maximizedTab?.id ?? undefined}
           data-maximized-tabset={maximizedTabSet?.id ?? undefined}
           data-preview-layout={previewLayoutInteractionNode
                                ? _.isArray(previewLayoutInteractionNode)
                                  ? previewLayoutInteractionNode.length
                                  : previewLayoutInteractionNode.id
                                :/* previewPreset && previewPreset._id != settings.preset ? previewPreset._id :*/ undefined}
           data-dragging-type={draggingNode?.type}
           data-hover-tab={model?.hoverTab?.id ?? undefined}
           style={cssVariables}
      >
        <ErrorBoundary
          errorContent={err => <ErrorOnFlexLayoutLoadContainer />}
        >
        {model && visible ?
         <FL.Layout
           ref={setRef}
           model={model}
           realtimeResize
           factory={renderNamedComponent}
           onRenderTab={onRenderTab}
           onRenderTabSet={onRenderTabSet}
           onAction={onAction}
           iconFactory={iconFactory}
           onModelChange={onModelChange}
         /> : <div data-has-model={model != null} data-visible={visible}/>}
        </ErrorBoundary>
        <FlexLayoutPreview/>
        {/*{isMaximized && <RightSidebar isGlobalSidebar />}*/}

        {/*<MaximizedTabPane />*/}

        <Dialog isOpen={currentReport?.isEditing || isSaveAsDialogOpen}
                title={`${saveAsDialogReport ? `Saving a copy of ` : `Edit`} '${currentReport?.displayName}'`}
                icon={isSaveAsDialogOpen ? 'floppy-disk' : 'edit'}
                onClose={cancelReportDialog} >
          <ReportPropertiesPanel/>
        </Dialog>
      </div>
    </FlexLayoutStoreContext.Provider>)
})

/// endregion

// rightIcon = tabStore?.rightIcon,
// rightIconTooltip = tabStore?.rightIconTooltip

//  {rightIcon && <Popover2
//           interactionKind='hover'
//           className={css.rightIcon}
//           hoverCloseDelay={1000}
//           minimal
//           position='bottom'
//       content={<Menu>
//         <SwitchFlexCatalogRootViewModeMenuItems catalogRootTab={tabStore} />
//       </Menu>}>
//           {/*<Tooltip2 content={rightIconTooltip}>*/}
//           <Icon icon={rightIcon} />
//       {/*</Tooltip2>*/}
//       </Popover2>}<div className={css.tabLeading}>{_.isString ? <Icon icon={icon}/> : icon}</div>


const MaximizedTabPane = observer(function MaximizedTabPane() {
  const {flexLayout: {isMaximized, everMaximized, lastMaximizedTab, renderNamedComponent}} = useStores()

  if (!everMaximized) return null

  return <div className={css.maximizedTabPanel} data-visible={isMaximized}>
    {lastMaximizedTab && renderNamedComponent(lastMaximizedTab)}
  </div>
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/FlexLayout.tsx